import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import {
	CONSTANT_TOKEN_REGEX,
	findFunctionCallRanges,
	OPERATOR_TOKEN_REGEX,
	RESOURCE_TOKEN_REGEX,
} from "../../domain/formula-grammar";

function matchAbsoluteRanges(
	regex: RegExp,
	text: string,
	pos: number
): Array<{ start: number; end: number }> {
	const flags = regex.global ? regex.flags : `${regex.flags}g`;
	const re = new RegExp(regex.source, flags);
	const out: Array<{ start: number; end: number }> = [];
	let m: RegExpExecArray | null;
	while ((m = re.exec(text))) {
		out.push({
			start: pos + m.index,
			end: pos + m.index + m[0].length,
		});
	}
	return out;
}

function rangesOverlapBlock(
	start: number,
	end: number,
	blocked: Array<[number, number]>
): boolean {
	return blocked.some(([s, e]) => start < e && end > s);
}

/** Non-overlapping gaps inside [fs, fe) after removing blocked intervals. */
function gapsExcludingBlocked(
	fs: number,
	fe: number,
	blocked: Array<[number, number]>
): Array<[number, number]> {
	const overlapping = blocked
		.filter(([s, e]) => s < fe && e > fs)
		.map(([s, e]) => [Math.max(fs, s), Math.min(fe, e)] as [number, number])
		.sort((a, b) => a[0] - b[0]);

	const merged: Array<[number, number]> = [];
	for (const iv of overlapping) {
		const last = merged[merged.length - 1];
		if (!last || last[1] < iv[0]) merged.push([iv[0], iv[1]]);
		else last[1] = Math.max(last[1], iv[1]);
	}

	const gaps: Array<[number, number]> = [];
	let cur = fs;
	for (const [s, e] of merged) {
		if (cur < s) gaps.push([cur, s]);
		cur = Math.max(cur, e);
	}
	if (cur < fe) gaps.push([cur, fe]);
	return gaps;
}

/**
 * Highlights resources, constants, functions, and operators with distinct
 * CSS classes. Function spans exclude `{…}`, `"…"`, and operators so those
 * keep their own colors (nested span fallback in CSS if PM nests wrappers).
 */
export const FormulaTokenHighlight = Extension.create({
	name: "formula-token-highlight",

	addProseMirrorPlugins() {
		return [
			new Plugin({
				key: new PluginKey("formula-token-highlight"),
				props: {
					decorations: ({ doc }) => {
						const decorations: Decoration[] = [];

						doc.descendants((node, pos) => {
							if (!node.isText || !node.text) return;

							const text = node.text;

							const resourceRanges = matchAbsoluteRanges(
								RESOURCE_TOKEN_REGEX,
								text,
								pos
							);
							const constantRanges = matchAbsoluteRanges(
								CONSTANT_TOKEN_REGEX,
								text,
								pos
							);
							const rcBlocked: Array<[number, number]> = [
								...resourceRanges.map((r) => [r.start, r.end] as [number, number]),
								...constantRanges.map((r) => [r.start, r.end] as [number, number]),
							];

							const rawOperatorRanges = matchAbsoluteRanges(
								OPERATOR_TOKEN_REGEX,
								text,
								pos
							);
							const operatorRanges = rawOperatorRanges.filter(
								(r) => !rangesOverlapBlock(r.start, r.end, rcBlocked)
							);

							const blockedForFunction: Array<[number, number]> = [
								...rcBlocked,
								...operatorRanges.map((r) => [r.start, r.end] as [number, number]),
							];

							for (const r of resourceRanges) {
								decorations.push(
									Decoration.inline(r.start, r.end, { class: "formula-resource-token" })
								);
							}
							for (const r of constantRanges) {
								decorations.push(
									Decoration.inline(r.start, r.end, { class: "formula-constant-token" })
								);
							}
							for (const r of operatorRanges) {
								decorations.push(
									Decoration.inline(r.start, r.end, { class: "formula-operator-token" })
								);
							}

							for (const fRange of findFunctionCallRanges(text)) {
								const fs = pos + fRange.start;
								const fe = pos + fRange.end;
								for (const [gs, ge] of gapsExcludingBlocked(fs, fe, blockedForFunction)) {
									if (ge <= gs) continue;
									decorations.push(
										Decoration.inline(gs, ge, { class: "formula-function-token" })
									);
								}
							}
						});

						return DecorationSet.create(doc, decorations);
					},
				},
			}),
		];
	},
});
